import { NO_EPG_ID, type Range } from '$/epg/programs'
import { CatchupMode, ONE_MINUTE_MS } from '@/constants'
import {
  AnalyticsContentType,
  TvChannelWithEpg,
  type EpgInfo,
  type LiveChannel,
} from '@setplex/tria-api'
import { produce } from 'immer'
import {
  getAnalyticsProgramInfo,
  type AnalyticsProgramInfoInput,
} from '~/shared/analytics'
import { NO_EPG_DATA } from '~/shared/constants'
import { convertToAnalyticsTitle } from '~/shared/helpers'
import {
  HALF_HOUR_MS,
  ONE_HOUR_MS,
  ONE_MINUTE_IN_SEC,
  ONE_SECOND_MS,
} from '../constants'
import { CurrentProgram } from '../types'
import {
  formatDateToLabelInfo,
  formatTimestampWithTimezoneOffset,
  getDayLabelFromDate,
} from './date'

export const generateId = (channelEpgId: string, start: number, stop: number) =>
  `${channelEpgId}${start}${stop}`

export const generateEmptyProgram = (
  start: number,
  stop: number,
  channelEpgId: string
): EpgInfo => ({
  id: generateId(channelEpgId, start, stop),
  channelEpgId,
  name: undefined,
  description: undefined,
  isEmpty: true,
  startMs: start,
  stopMs: stop,
  actualStartMs: start,
  actualStopMs: stop,
  actualStartSec: start / ONE_SECOND_MS,
  actualStopSec: stop / ONE_SECOND_MS,
  durationMs: stop - start,
  actualDurationMs: stop - start,
  categories: [],
  directors: [],
  actors: [],
})

// Hourly programs are not empty as they are used to show catchup interval
export const generateEmptyHourlyProgram = ({
  start,
  stop,
  channelEpgId,
  channelTitle,
}: {
  start: number
  stop: number
  channelEpgId: string
  channelTitle: string
}): EpgInfo => ({
  id: generateId(channelEpgId, start, stop),
  channelEpgId,
  name: `Program ${channelTitle}`,
  description: undefined,
  isEmpty: true,
  startMs: start,
  stopMs: stop,
  actualStartMs: start,
  actualStopMs: stop,
  actualStartSec: start / ONE_SECOND_MS,
  actualStopSec: stop / ONE_SECOND_MS,
  durationMs: stop - start,
  actualDurationMs: stop - start,
  categories: [],
  directors: [],
  actors: [],
})

export const generateHourlyPrograms = (
  start: number,
  epg: EpgInfo[],
  channelTitle: string
) => {
  // For hourly catchup, split non-live programs into 1 hour segments
  const now = Date.now()
  const isMoreThan30MinIntoCurrentHour = new Date(now).getMinutes() >= 30
  const timeslotThatStartShowingCurrentProgram =
    Math.floor(now / HALF_HOUR_MS) * HALF_HOUR_MS -
    (isMoreThan30MinIntoCurrentHour ? HALF_HOUR_MS : 0)

  // Create hourly programs from period start until now
  const hourlyPrograms = []

  let segmentStart = start
  let segmentEnd
  while (segmentStart < timeslotThatStartShowingCurrentProgram) {
    segmentEnd = segmentStart + ONE_HOUR_MS
    const hourProgram = generateEmptyHourlyProgram({
      start: segmentStart,
      stop: segmentEnd,
      channelEpgId: epg[0].channelEpgId,
      channelTitle: channelTitle,
    })
    hourlyPrograms.push(hourProgram)

    segmentStart = segmentEnd
  }

  const currentProgram = epg.find(
    (program) => program.actualStartMs <= now && program.actualStopMs > now
  )

  return [
    ...hourlyPrograms,
    // overwrite actualStartMs and startMs to show current program (all previous period would be catchups)
    {
      ...currentProgram,
      actualStartMs: segmentStart,
      actualStartSec: segmentStart / ONE_SECOND_MS,
      startMs: segmentStart,
    },
  ]
}

/**
 * Extends channel with epg data
 */
export const extendWithProgram =
  (
    programs: ReadonlyMap<string, readonly Readonly<EpgInfo>[]>,
    period?: Range,
    catchupEnabled?: boolean | null
  ) =>
  (channel: Readonly<LiveChannel>): TvChannelWithEpg => {
    let epg = programs.get(channel.channelEpgId ?? NO_EPG_ID) || []

    if (epg.length === 0) {
      // extending with empty program can be happen when a request for programs has failed
      // or when an empty object is returned instead of a response
      epg = [
        generateEmptyProgram(period?.[0] ?? 0, period?.[1] ?? 0, NO_EPG_ID),
      ]
    }

    const { enabled = false, shift = 0 } = channel.timeshift || {}
    const userTimezoneOffset = -new Date().getTimezoneOffset()

    let timeshift = 0
    let differenceInHours = 0
    if (enabled) {
      timeshift = (shift - userTimezoneOffset) * ONE_MINUTE_IN_SEC
      differenceInHours = (shift - userTimezoneOffset) / 60
    }

    const isHourlyCatchup =
      channel.catchUp?.mode === 'HOURLY' &&
      channel.catchUp?.enabled &&
      catchupEnabled

    if (isHourlyCatchup) {
      epg = generateHourlyPrograms(
        period?.[0] ?? 0,
        [...epg],
        channel.title ?? ''
      )
    }

    // if channel is in hourly catchup mode, we don't need to shift programs, catchup interval will be applied in model
    const programsTimeShift = isHourlyCatchup ? 0 : timeshift

    return {
      ...channel,
      programs: epg.map(
        produce((draft) => {
          draft.title = `${channel.number}. ${channel.title}`
          draft.catchUp = catchupEnabled
            ? {
                ...channel.catchUp,
                duration: (channel.catchUp?.duration || 0) - differenceInHours,
              }
            : undefined
          draft.actualStartSec = draft.actualStartSec + programsTimeShift
          draft.actualStopSec = draft.actualStopSec + programsTimeShift
          draft.startMs = draft.startMs + programsTimeShift * ONE_SECOND_MS
          draft.stopMs = draft.stopMs + programsTimeShift * ONE_SECOND_MS
          draft.actualStartMs =
            draft.actualStartMs + programsTimeShift * ONE_SECOND_MS
          draft.actualStopMs =
            draft.actualStopMs + programsTimeShift * ONE_SECOND_MS
        })
      ),
    }
  }

export const getCurrentProgram = ({
  activeChannelWithFormattedEPG,
  epgIsReady,
  catchupInterval,
  today,
  playingRewindData,
  timeshift,
}: {
  activeChannelWithFormattedEPG: TvChannelWithEpg | null
  epgIsReady: boolean
  catchupInterval: { startTime: number; endTime: number } | null
  today: { year: number; month: number; day: number }
  playingRewindData: {
    isRewinded: boolean
    programStartMs: number | null
  }
  timeshift: {
    enabled: boolean
    shift: number
  } | null
}): CurrentProgram => {
  if (!epgIsReady) return null
  const nowMs = Date.now()

  const { isRewinded = false, programStartMs = null } = playingRewindData || {}
  const { enabled: isTimeshiftEnabled, shift = 0 } =
    activeChannelWithFormattedEPG?.timeshift || {}
  let timeshiftTimeMs = 0
  if (isTimeshiftEnabled) {
    const userTimezoneOffset = -new Date().getTimezoneOffset()
    timeshiftTimeMs = (shift - userTimezoneOffset) * ONE_MINUTE_MS
  }

  if (!catchupInterval) {
    // find the rewind program
    if (isRewinded && programStartMs && programStartMs > 0) {
      const liveRewindDurationMs =
        (activeChannelWithFormattedEPG?.liveRewind?.duration || 0) * ONE_HOUR_MS

      const rewindProgram = activeChannelWithFormattedEPG?.programs?.find(
        (item) => {
          return (playingRewindData.programStartMs || 0) === item.actualStartMs
        }
      )
      const maxLeftStartSec = Math.round(
        (nowMs - liveRewindDurationMs) / ONE_SECOND_MS
      )
      const maxStopSec = Math.round(nowMs / ONE_SECOND_MS)
      if (rewindProgram) {
        const {
          actualStartMs,
          actualStopMs,
          isEmpty,
          labels,
          actualStartSec,
          actualStopSec,
          actualDurationMs,
          isLive,
          programDescription,
        } = rewindProgram
        const programTitle = rewindProgram?.programTitle || NO_EPG_DATA
        const startEndLabel = isEmpty ? '' : labels?.startEndLabel || ''
        const startProgramWithRewindDurationSec = isEmpty
          ? maxLeftStartSec
          : actualStartSec || 0
        const stopProgramWithRewindDurationSec = isEmpty
          ? maxStopSec
          : actualStopSec || 0

        const programStart = formatTimestampWithTimezoneOffset(
          (actualStartMs || 0) - timeshiftTimeMs,
          false
        )

        return {
          channelId: activeChannelWithFormattedEPG?.id ?? null,
          programId: rewindProgram.programId || rewindProgram.id,
          programTitle,
          labels: startEndLabel
            ? [startEndLabel, programTitle]
            : [programTitle],
          isLive,
          actualStartMs: actualStartMs || 0,
          actualStopMs: actualStopMs || 0,
          actualStartSec: startProgramWithRewindDurationSec,
          actualStopSec: stopProgramWithRewindDurationSec,
          actualDurationMs: actualDurationMs || 0,
          maxRewindStamp: activeChannelWithFormattedEPG?.liveRewind?.enabled
            ? maxLeftStartSec
            : null,
          isEmpty,
          programStartForAnalytics: isEmpty ? '' : programStart,
          programTitleForAnalytics: convertToAnalyticsTitle(
            programTitle,
            false
          ),
          contentTypeForAnalytics: AnalyticsContentType.liveRewind,
          durationForAnalytics: isEmpty
            ? '0'
            : String((actualDurationMs || 0) / ONE_SECOND_MS),
          isRewinded,
          programDescription: programDescription || '',
        }
      }
    }

    // Live program
    const liveProgram = activeChannelWithFormattedEPG?.programs.find(
      (item) => item.isLive
    )

    const {
      actualStartMs = 0,
      actualStopMs,
      actualDurationMs,
      actualStartSec,
      actualStopSec,
      isEmpty,
      programDescription,
    } = liveProgram || {}

    const programTitle = liveProgram?.programTitle || NO_EPG_DATA
    const startEndLabel = isEmpty
      ? ''
      : liveProgram?.labels?.startEndLabel || ''
    const liveRewindDurationMs =
      (activeChannelWithFormattedEPG?.liveRewind?.duration || 0) * ONE_HOUR_MS
    const maxLeftStartSec = Math.round(
      (nowMs - liveRewindDurationMs) / ONE_SECOND_MS
    )
    const maxStopSec = Math.round(nowMs / ONE_SECOND_MS)

    const startProgramWithRewindDurationSec = isEmpty
      ? maxLeftStartSec
      : actualStartSec || 0
    const stopProgramWithRewindDurationSec = isEmpty
      ? maxStopSec
      : actualStopSec || 0
    // Fields used for analytics only
    const programStart = formatTimestampWithTimezoneOffset(
      (actualStartMs || 0) - timeshiftTimeMs,
      false
    )

    return {
      channelId: activeChannelWithFormattedEPG?.id ?? null,
      programId: liveProgram?.programId,
      programTitle,
      programStartForAnalytics: isEmpty ? '' : programStart,
      programTitleForAnalytics: convertToAnalyticsTitle(programTitle, false),
      contentTypeForAnalytics: AnalyticsContentType.live,
      durationForAnalytics: isEmpty
        ? '0'
        : String((actualDurationMs || 0) / ONE_SECOND_MS),
      labels: startEndLabel ? [startEndLabel, programTitle] : [programTitle],
      // it's always true to avoid flickering not live player ui while we are waiting for channel info (actual for live player from rows and My List)
      isLive: true, // true, because isLive is true for rewind program too
      actualStartMs: actualStartMs || 0,
      actualStopMs: actualStopMs || 0,
      actualStartSec: startProgramWithRewindDurationSec,
      actualStopSec: stopProgramWithRewindDurationSec,
      actualDurationMs: actualDurationMs || 0,
      maxRewindStamp: activeChannelWithFormattedEPG?.liveRewind?.enabled
        ? maxLeftStartSec
        : null,
      isEmpty,
      isRewinded,
      programDescription: programDescription || '',
    }
  }

  // Catchup program
  const { startTime, endTime } = catchupInterval
  if (!startTime || !endTime || !activeChannelWithFormattedEPG) return null

  const timeshiftedStartTime =
    startTime + (timeshift?.shift || 0) * ONE_SECOND_MS
  const timeshiftedEndTime = endTime + (timeshift?.shift || 0) * ONE_SECOND_MS

  // For the player, we receive the channel's original formatted programs without generating hourly segments
  // (unlike the EPG board which uses hourly segments). If this channel uses hourly catchup mode,
  // the program may be undefined since we don't have the hourly segments here.
  // Note: A channel can only use either EPG-based or hourly-based catchup - these modes cannot be mixed.

  // Find the program based on start/end times
  const program = activeChannelWithFormattedEPG.programs.find(
    (item) =>
      timeshiftedStartTime === item.actualStartMs &&
      timeshiftedEndTime === item.actualStopMs
  )

  const todayStartMs = new Date(today.year, today.month, today.day).getTime()

  const startEndLabel = formatDateToLabelInfo(
    timeshiftedStartTime,
    timeshiftedEndTime
  )
  const programTitle =
    program?.programTitle || `Program ${activeChannelWithFormattedEPG.title}`
  const dateLabel = getDayLabelFromDate(startTime, todayStartMs)

  // Fields used for analytics only
  const programStart = formatTimestampWithTimezoneOffset(startTime || 0, false)

  const isHourly =
    activeChannelWithFormattedEPG.catchUp?.mode === CatchupMode.Hourly

  return {
    channelId: activeChannelWithFormattedEPG?.id ?? null,
    programId:
      program?.programId ??
      generateId(
        activeChannelWithFormattedEPG.channelEpgId || '',
        startTime,
        endTime
      ), // generate id for hourly catchup program
    programTitle,
    programStartForAnalytics: program?.isEmpty ? '' : programStart,
    programTitleForAnalytics: convertToAnalyticsTitle(programTitle, isHourly),
    contentTypeForAnalytics: AnalyticsContentType.catchup,
    labels: [dateLabel, startEndLabel, programTitle],
    programDescription: program?.programDescription || '',
    startTime,
    endTime,
    actualStartMs: timeshiftedStartTime,
    actualStopMs: timeshiftedEndTime,
    isHourly: activeChannelWithFormattedEPG.catchUp?.mode === 'HOURLY',
    isCatchup: activeChannelWithFormattedEPG.catchUp?.enabled,
  }
}

type Timeshift = {
  enabled: boolean
  shift: number
}

export const getTimeshiftTimeMs = (timeshift?: Timeshift | null): number => {
  const { enabled: isTimeshiftEnabled, shift = 0 } = timeshift || {}

  if (!isTimeshiftEnabled) {
    return 0
  }

  const userTimezoneOffset = -new Date().getTimezoneOffset()

  return (shift - userTimezoneOffset) * ONE_MINUTE_MS
}

type BuildEpgProgramEventDataParams = {
  channelId: string | number | null | undefined
  channelTitle: string | undefined
  channelOrderPosition: number
  timeshift?: Timeshift | null
  program: AnalyticsProgramInfoInput & {
    actualStartMs: number
    actualStopMs: number
  }
}

export const buildEpgProgramEventData = ({
  channelId,
  channelTitle,
  channelOrderPosition,
  timeshift,
  program,
}: BuildEpgProgramEventDataParams) => {
  const timeshiftTimeMs = getTimeshiftTimeMs(timeshift)
  const { actualStartMs, actualStopMs } = program

  return {
    channelId: String(channelId ?? ''),
    channelTitle: String(channelTitle ?? ''),
    channelOrderPosition: String(channelOrderPosition),
    programStart: String(
      formatTimestampWithTimezoneOffset(
        (actualStartMs || 0) - timeshiftTimeMs,
        false
      )
    ),
    programEnd: String(
      formatTimestampWithTimezoneOffset(
        (actualStopMs || 0) - timeshiftTimeMs,
        false
      )
    ),
    ...getAnalyticsProgramInfo(program),
  }
}
